import type {
  LockerverseAuction as Auction,
  LockerverseAuctionItem,
  LockerverseAuctionStatus,
} from "@lockerverse/sdk/auctions";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  formatAuctionBoundary,
  getAuctionDisplayStatus,
} from "./auction-display.ts";
import { useAuctionVisibility } from "./use-auction-visibility.ts";
export const STATUS_LABELS = {
  active: "Live now",
  draft: "Coming soon",
  finished: "Closed",
} satisfies Record<LockerverseAuctionStatus, string>;
export function CatalogImage({
  images,
  fallback,
  name,
}: {
  images?: string[];
  fallback?: string | null | undefined;
  name: string;
}) {
  const photos = useMemo(() => {
    const unique = [...new Set(images?.filter(Boolean))];
    if (!unique.length && fallback) {
      unique.push(fallback);
    }
    return unique;
  }, [images, fallback]);
  const [index, setIndex] = useState(0);
  const { ref: imageRef, visible } = useAuctionVisibility<HTMLDivElement>();
  const [reducedMotion, setReducedMotion] = useState(true);
  const [interacting, setInteracting] = useState(false);
  const loaded = useRef(new Set<string>());
  const count = photos.length;
  const current = count ? index % count : 0;
  useEffect(() => {
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotion = () => setReducedMotion(motion.matches);
    updateMotion();
    motion.addEventListener("change", updateMotion);
    const card = imageRef.current?.closest("article");
    const updateInteraction = () =>
      setInteracting(Boolean(card?.matches(":hover, :focus-within")));
    const leaveFocus = (event: FocusEvent) =>
      setInteracting(
        Boolean(
          card?.matches(":hover") ||
            (event.relatedTarget instanceof Node &&
              card?.contains(event.relatedTarget))
        )
      );
    updateInteraction();
    card?.addEventListener("mouseenter", updateInteraction);
    card?.addEventListener("mouseleave", updateInteraction);
    card?.addEventListener("focusin", updateInteraction);
    card?.addEventListener("focusout", leaveFocus);
    return () => {
      motion.removeEventListener("change", updateMotion);
      card?.removeEventListener("mouseenter", updateInteraction);
      card?.removeEventListener("mouseleave", updateInteraction);
      card?.removeEventListener("focusin", updateInteraction);
      card?.removeEventListener("focusout", leaveFocus);
    };
  }, [imageRef]);
  useEffect(() => {
    if (count < 2 || !visible || reducedMotion || interacting) {
      return;
    }
    const timer = window.setInterval(() => {
      setIndex((previous) =>
        loaded.current.has(photos[(previous + 1) % count] ?? "")
          ? (previous + 1) % count
          : previous
      );
    }, 5000);
    return () => window.clearInterval(timer);
  }, [count, photos, visible, reducedMotion, interacting]);
  return (
    <div className="lockerverse-auctions__catalog-images" ref={imageRef}>
      {count
        ? [current, ...(count > 1 ? [(current + 1) % count] : [])].map(
            (photo) => (
              <img
                alt={photo === current ? name : ""}
                aria-hidden={photo !== current}
                className="lockerverse-auctions__catalog-photo"
                data-visible={photo === current}
                height={500}
                key={photos[photo]}
                loading="lazy"
                onLoad={() => loaded.current.add(photos[photo] ?? "")}
                src={photos[photo]}
                width={640}
              />
            )
          )
        : null}
    </div>
  );
}
export function Metric({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="lockerverse-auctions__catalog-metric">
      <dt>
        {icon}
        {label}
      </dt>
      <dd>{value}</dd>
    </div>
  );
}
export function Timing({
  auction,
  status = getAuctionDisplayStatus(auction),
  item,
}: {
  auction: Auction;
  status?: LockerverseAuctionStatus;
  item?: LockerverseAuctionItem;
}) {
  let label = "";
  if (status === "draft") {
    label =
      !item && Date.parse(auction.startAt) <= Date.now()
        ? "Next opens "
        : "Opens ";
  }
  if (status === "active") {
    label = item ? "Closes " : "Ends ";
  }
  return (
    <span className="lockerverse-auctions__catalog-timing">
      {label}
      {formatAuctionBoundary(auction, status, item)}
    </span>
  );
}
